import dayjs from "dayjs";

import { getEffectiveAdminTimezone } from "../../../../utils/dateService";

import type { PaymentDirection, PaymentRow } from "./mockPayments";

export type PaymentDirectionFilter = PaymentDirection | "both";
export type PaymentSortBy = "amount" | "total_balance";
export type PaymentSortOrder = "asc" | "desc";

export type PaymentQueryInput = {
  searchValue?: string;
  direction?: PaymentDirectionFilter;
  minPayment?: string;
  maxPayment?: string;
  startDate?: string | null;
  endDate?: string | null;
  sortBy?: PaymentSortBy | null;
  sortOrder?: PaymentSortOrder | null;
  page?: number;
  pageSize?: number;
};

export type PaymentQueryResult = {
  items: PaymentRow[];
  total: number;
};

const toNumber = (value: string | number | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseBound = (value: string | undefined): number | null => {
  if (value == null || value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toAdminDate = (timestamp: string | number): string =>
  dayjs(timestamp).tz(getEffectiveAdminTimezone()).format("YYYY-MM-DD");

export const applyPaymentQuery = (
  payments: PaymentRow[],
  query: PaymentQueryInput = {},
): PaymentQueryResult => {
  const searchValue = (query.searchValue ?? "").trim().toLowerCase();
  const direction = query.direction ?? "both";
  const minPayment = parseBound(query.minPayment);
  const maxPayment = parseBound(query.maxPayment);
  const page = query.page && query.page > 0 ? query.page : 1;
  const pageSize =
    query.pageSize && query.pageSize > 0 ? query.pageSize : payments.length || 1;

  let filtered = payments.filter((payment) => {
    if (
      searchValue &&
      !payment.transaction_id.toLowerCase().includes(searchValue)
    ) {
      return false;
    }

    if (direction !== "both" && payment.payment_direction !== direction) {
      return false;
    }

    const amount = toNumber(payment.amount);
    if (minPayment != null && amount < minPayment) {
      return false;
    }
    if (maxPayment != null && amount > maxPayment) {
      return false;
    }

    if (query.startDate || query.endDate) {
      const paymentDate = toAdminDate(payment.timestamp);
      if (query.startDate && paymentDate < query.startDate) {
        return false;
      }
      if (query.endDate && paymentDate > query.endDate) {
        return false;
      }
    }

    return true;
  });

  if (query.sortBy && query.sortOrder) {
    const multiplier = query.sortOrder === "asc" ? 1 : -1;
    const field = query.sortBy;
    filtered = [...filtered].sort((left, right) => {
      const delta = toNumber(left[field]) - toNumber(right[field]);
      return delta * multiplier;
    });
  }

  const start = (page - 1) * pageSize;
  return {
    items: filtered.slice(start, start + pageSize),
    total: filtered.length,
  };
};
